import * as React from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import {
  Calendar,
  DollarSign,
  BookOpen,
  Receipt,
  FileText,
  Save,
  X,
} from 'lucide-react';
import useFormData from '@/hooks/useFormData';
import { useGetBenefitCategoryQuery } from '@/store/benefitCategoryApi';
import {
  useAddBenefitMutation,
  useUpdateBenefitMutation,
} from '@/store/benefitApi';
import { useSelector } from 'react-redux';
import { RootState } from '@/types';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

interface Benefit {
  ledger: string;
  remark?: string;
  amount: string;
  date: string;
}

const AddBenefitForm = ({ editBenefit = null, setEdit = () => {}, onClose = null }) => {
  const { category } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.user);
  const { data: allLedgers, isLoading: ledgersLoading } =
    useGetBenefitCategoryQuery(user?.token);

  const [addBenefitFn, { isSuccess: addSuccess, isLoading: addLoading }] = useAddBenefitMutation();
  const [updateBenefitFn, { isSuccess: updateSuccess, isLoading: updateLoading }] =
    useUpdateBenefitMutation();

  // Format initial data for the form
  const initialData = React.useMemo(() => {
    if (editBenefit) {
      return {
        ledger: editBenefit.ledger._id,
        remark: editBenefit.remark,
        amount: editBenefit.amount.toString(),
        date: new Date(editBenefit.date).toISOString().split('T')[0],
      };
    }
    const preselectedLedger = allLedgers?.find((option) => option.ledger === category);
    return {
      ledger: preselectedLedger ? preselectedLedger._id : '',
      remark: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
    };
  }, [editBenefit, allLedgers, category]);

  React.useEffect(() => {
    if (editBenefit) {
      setEdit(editBenefit);
    }
    return () => {
      setEdit(null);
    };
  }, [editBenefit]);

  const { formData, handleChange, resetForm, setFormData } =
    useFormData<Benefit>(initialData);

  // Keep form data in sync if initialData changes (e.g. when ledgers load)
  React.useEffect(() => {
    setFormData(initialData);
  }, [initialData, setFormData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.ledger) {
      toast.error('Please select a category');
      return;
    }

    const refactoredData = {
      ...formData,
      amount: Number(formData.amount),
      date: new Date(formData.date).toLocaleDateString(),
      month: new Date(formData.date).getMonth() + 1,
      year: new Date(formData.date).getFullYear(),
    };

    if (editBenefit) {
      await updateBenefitFn({
        Token: user?.token,
        benefitId: editBenefit._id,
        data: refactoredData,
      });
    } else {
      await addBenefitFn({
        Token: user?.token,
        data: refactoredData,
      });
    }
  };

  React.useEffect(() => {
    if (addSuccess || updateSuccess) {
      resetForm();
      toast.success(
        editBenefit
          ? 'Benefit Updated Successfully'
          : 'Benefit Added Successfully'
      );
      if (onClose) {
        onClose();
      } else {
        navigate('/benefits');
      }
    }
  }, [addSuccess, updateSuccess]);

  const isSaving = addLoading || updateLoading;

  return (
    <Card className="overflow-auto max-h-[85vh] rounded-lg border shadow-lg bg-card">
      <CardHeader className="border-b pb-4">
        <CardTitle className="text-2xl flex items-center gap-2 text-emerald-600">
          <Receipt className="w-6 h-6" />
          <h1>{editBenefit ? 'Edit Benefit Inflow' : 'Add New Benefit Inflow'}</h1>
        </CardTitle>
        <p className="text-sm text-muted-foreground">Enter incoming amount details below</p>
      </CardHeader>

      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ledger Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <BookOpen className="h-4 w-4 text-emerald-600" />
                Benefit Category / Ledger <span className="text-red-500">*</span>
              </Label>

              {category ? (
                <div className="p-2 border rounded-md bg-muted text-foreground">
                  {allLedgers?.find((option) => option.ledger === category)
                    ?.ledger || 'Category not found'}
                </div>
              ) : (
                <Select
                  value={formData.ledger}
                  required
                  onValueChange={(value) =>
                    handleChange({ id: 'ledger', value })
                  }
                >
                  <SelectTrigger className="w-full text-foreground bg-background border">
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {allLedgers?.map((option) => (
                      <SelectItem key={option._id} value={option._id}>
                        {option.ledger}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Amount Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-muted-foreground sm:flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-600" />
                Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">₹</span>
                <Input
                  type="number"
                  id="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  placeholder="0.00"
                  className="w-full p-3 pl-8 text-foreground bg-background border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Date Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-muted-foreground sm:flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-600" />
                Inflow Date <span className="text-red-500">*</span>
              </label>
              <Input
                required
                type="date"
                id="date"
                value={formData.date}
                onChange={handleChange}
                className="w-full p-3 text-foreground bg-background border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Remarks Textarea */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-muted-foreground sm:flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-600" />
              Remarks / Description <span className="text-red-500">*</span>
            </label>
            <Textarea
              required
              id="remark"
              value={formData.remark}
              onChange={handleChange}
              rows={4}
              className="w-full p-3 text-foreground bg-background border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              placeholder="Enter remarks or details of the incoming amount..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-lg border text-muted-foreground font-medium hover:bg-muted transition-all flex items-center gap-2"
              >
                Cancel
              </button>
            ) : (
              <button
                type="button"
                onClick={() => resetForm()}
                className="px-5 py-2.5 rounded-lg border text-muted-foreground font-medium hover:bg-muted transition-all flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}

            <button
              type="submit"
              disabled={ledgersLoading || isSaving}
              className={`px-5 py-2.5 rounded-lg text-white font-medium transition-all flex items-center gap-2
                ${
                  ledgersLoading || isSaving
                    ? 'bg-emerald-400 cursor-not-allowed'
                    : 'bg-emerald-600 hover:bg-emerald-700 active:scale-95'
                }`}
            >
              <Save className="w-4 h-4" />
              {isSaving
                ? 'Saving...'
                : editBenefit
                ? 'Update Benefit'
                : 'Save Benefit'}
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddBenefitForm;
